import React, { useState, useRef } from "react";
import Logo from "../../assets/Logo.svg";
import bgImage from "../../assets/back.png";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// APIs
import {
  forgetPassword,   // email par OTP bhejne ke liye
  verifyOtp,        // OTP verify ke liye
  changePasswords,   // password update ke liye
} from "../../ApiController/ApiService";

// ✅ Input Field Component
const OutlinedInput = ({ label, type, placeholder, value, readOnly, onChange }) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputType =
    type === "password" ? (showPassword ? "text" : "password") : type;

  return (
    <div className="relative mb-6">
      <label className="absolute -top-2 left-3 px-1 bg-white text-sm text-[#1E0909] z-[1]">
        {label}
      </label>
      <div className="relative">
        <input
          type={inputType}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          className="w-[546px] h-[64px] border border-[#858080] rounded-lg px-3 pt-4 pb-2 text-base text-black bg-white placeholder:text-[#858080] focus:outline-none focus:border-purple-600"
        />
        {type === "password" && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#858080] focus:outline-none"
          >
            {showPassword ? (
              <AiOutlineEyeInvisible size={24} />
            ) : (
              <AiOutlineEye size={24} />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// ✅ OTP Input component (6 boxes)
const OtpInput = ({ value, onChange }) => {
  const inputsRef = useRef([]);

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    if (!val) return;
    const otpArray = value.split("");
    otpArray[idx] = val[val.length - 1];
    const newOtp = otpArray.join("");
    onChange(newOtp);

    if (idx < 5 && val) {
      inputsRef.current[idx + 1].focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !value[idx] && idx > 0) {
      inputsRef.current[idx - 1].focus();
    }
  };

  return (
    <div className="flex justify-between mb-6 w-[80%] mx-auto">
      {Array(6)
        .fill("")
        .map((_, idx) => (
          <input
            key={idx}
            ref={(el) => (inputsRef.current[idx] = el)}
            type="text"
            maxLength={1}
            value={value[idx] || ""}
            onChange={(e) => handleChange(e, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className="w-12 h-12 border border-[#858080] rounded-lg text-center text-xl font-bold focus:outline-none focus:border-purple-600"
          />
        ))}
    </div>
  );
};

const ResetPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [userId, setUserId] = useState(""); // ✅ Save userId after OTP verification
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Step 1: Send Email
  const handleSendEmail = async () => {
    if (!email) {
      setError("Please enter your email");
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      const response = await forgetPassword({ email });
      // Swal.fire("OTP Sent!", `Use this 6-digit OTP: ${response.data.otp}`, "success");
      setStep(2);
    } catch (err) {
      setError(err.message || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setError("Enter a valid 6-digit OTP");
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      const response = await verifyOtp({ email, otp });


      console.log('res',response)
      setUserId(response.data.userId); // ✅ Save userId
      Swal.fire("Success!", "OTP verified successfully!", "success");
      setStep(3);
    } catch (err) {
      setError(err.message || "Invalid OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Change Password
  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      await changePasswords(userId, { newPassword, confirmPassword });
      Swal.fire("Success!", "Password reset successful!", "success").then(() => {
        navigate("/");
      });
    } catch (err) {
      setError(err.message || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen w-screen"
      style={{
        background: "radial-gradient(circle at center, #080625 100%, #6F1AFF 0%)",
      }}
    >
      <img src={Logo} alt="Dispax Logo" className="w-[88px] h-[33px] mb-6" />
      <div className="bg-white p-10 rounded-lg text-black w-[578px] min-h-[549px] flex flex-col items-center">
        <h2 className="text-[42px] font-extrabold text-[#000000] mb-5 text-center">
          Reset Password
        </h2>

        <div className="w-[546px] flex flex-col">
          {error && <p className="text-red-500 text-center mb-4">{error}</p>}

          {step === 1 && (
            <>
              <p className="text-center text-[#858080] mb-5">
                Enter your registered email to receive OTP
              </p>
              <OutlinedInput
                label="Email"
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                className="bg-[#6F1AFF] text-white p-2 w-full rounded-[10px] text-[20px] font-bold h-[64px]"
                onClick={handleSendEmail}
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send OTP"}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-center text-[#858080] mb-5">Enter the 6-digit OTP</p>
              <OtpInput value={otp} onChange={setOtp} />
              <button
                className="bg-[#6F1AFF] text-white p-2 w-full rounded-[10px] text-[20px] font-bold h-[64px]"
                onClick={handleVerifyOtp}
                disabled={isLoading}
              >
                {isLoading ? "Verifying..." : "Verify OTP"}
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-center text-[#858080] mb-5">Enter your new password</p>
              <OutlinedInput
                label="New Password"
                type="password"
                placeholder="Enter Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <OutlinedInput
                label="Confirm Password"
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                className="bg-[#6F1AFF] text-white p-2 w-full rounded-[10px] text-[20px] font-bold h-[64px]"
                onClick={handleChangePassword}
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : "Save & Login"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
